import React, { useState, useEffect } from 'react';
import { ExerciseDefinition, ExerciseType, MovementUnit, MuscleGroup, WorkoutTemplateData } from '../../../types';
import { apiServices } from '../../../api/services';
import { useAuth } from '../../../context/AuthContext';
import { BaseApiService } from '../../../api/services/BaseApiService';
import { useTranslation } from '../../../i18n/provider';
import styles from './CreateTemplateModal.module.css';

interface CreateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTemplateCreated: () => void;
  initialTemplate?: WorkoutTemplateData;
}

interface SelectedExercise {
  exerciseId: string;
  name: string;
  imageUrl?: string;
  // TODO.md E, item 5: needed to decide whether to show the hold-time input instead of reps/weight/unit.
  exerciseType?: ExerciseType;
  sets: number;
  reps: number;
  weight: number;
  unit: MovementUnit;
}

// TODO.md E, item 5: stretching/mobility exercises collect hold time (seconds, stored in `reps`)
// instead of reps @ weight.
const isHoldTimeExercise = (exerciseType?: ExerciseType): boolean =>
  exerciseType === 'stretching' || exerciseType === 'mobility';

const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
  isOpen,
  onClose,
  onTemplateCreated,
  initialTemplate,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
    const [selectedMuscleGroups, setSelectedMuscleGroups] = useState<MuscleGroup[]>([]);
  const [selectedExercises, setSelectedExercises] = useState<SelectedExercise[]>([]);
  const [availableExercises, setAvailableExercises] = useState<ExerciseDefinition[]>([]);
  const [searchResults, setSearchResults] = useState<ExerciseDefinition[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [displayedCount, setDisplayedCount] = useState(10);
  const [showExerciseSelector, setShowExerciseSelector] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const isEditMode = Boolean(initialTemplate);

  const muscleGroups: MuscleGroup[] = ['chest', 'back', 'shoulders', 'biceps', 'triceps', 'arms', 'legs', 'abs'];
  const LOAD_BATCH = 10;

  const formatMuscleGroupLabel = (group: string) => {
    const fallback = group.charAt(0).toUpperCase() + group.slice(1);
    return t(`muscleGroups.${group}`, { defaultValue: fallback });
  };

  useEffect(() => {
    if (isOpen) {
      const loadExercises = async () => {
        const exercises = await apiServices.exercises.getExercises();
        setAvailableExercises(exercises);
        setSearchResults([]);
        setDisplayedCount(LOAD_BATCH);
      };
      loadExercises();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (!initialTemplate) {
      setTemplateName('');
      setDescription('');
      setSelectedMuscleGroups([]);
      setSelectedExercises([]);
      setShowExerciseSelector(false);
      setSearchQuery('');
      setSearchResults([]);
      setDisplayedCount(LOAD_BATCH);
      return;
    }

    setTemplateName(initialTemplate.name || '');
    setDescription(initialTemplate.description || '');
    setSelectedMuscleGroups((initialTemplate.muscleGroups || []) as MuscleGroup[]);

    const byId = new Map(availableExercises.map((e) => [e.id, e] as const));
    const mapped = (initialTemplate.exercises || []).map((ex) => {
      const def = byId.get(ex.exerciseId);
      return {
        exerciseId: ex.exerciseId,
        name: def?.name || ex.exerciseId,
        imageUrl: def?.imageUrl,
        exerciseType: def?.exerciseType,
        sets: ex.sets,
        reps: ex.reps,
        weight: ex.weight,
        unit: ex.unit,
      } satisfies SelectedExercise;
    });

    setSelectedExercises(mapped);
    setShowExerciseSelector(false);
    setSearchQuery('');
    setSearchResults([]);
    setDisplayedCount(LOAD_BATCH);
  }, [isOpen, initialTemplate, availableExercises]);

  useEffect(() => {
    if (!isOpen) return;

    const query = searchQuery.trim();
    setDisplayedCount(LOAD_BATCH);

    if (!query) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    let cancelled = false;
    setIsSearching(true);

    apiServices.exercises.searchExercises(query)
      .then((results) => {
        if (!cancelled) {
          setSearchResults(results || []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSearchResults([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsSearching(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, searchQuery]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleMuscleGroupToggle = (group: MuscleGroup) => {
    setSelectedMuscleGroups(prev =>
      prev.includes(group)
        ? prev.filter(g => g !== group)
        : [...prev, group]
    );
  };

  const handleAddExercise = (exercise: ExerciseDefinition) => {
    const holdTime = isHoldTimeExercise(exercise.exerciseType);
    const newExercise: SelectedExercise = {
      exerciseId: exercise.id,
      name: exercise.name,
      imageUrl: exercise.imageUrl,
      exerciseType: exercise.exerciseType,
      sets: 3,
      reps: holdTime ? 30 : 5,
      weight: 0,
      unit: holdTime ? 'sec' : 'kg',
    };
    setSelectedExercises(prev => [...prev, newExercise]);
    setShowExerciseSelector(false);
    setSearchQuery('');
    setSearchResults([]);
    setDisplayedCount(LOAD_BATCH);
  };

  const handleRemoveExercise = (index: number) => {
    setSelectedExercises(prev => prev.filter((_, i) => i !== index));
  };

  const handleExerciseChange = (index: number, field: keyof SelectedExercise, value: any) => {
    setSelectedExercises(prev =>
      prev.map((ex, i) => (i === index ? { ...ex, [field]: value } : ex))
    );
  };

  const handleSubmit = async () => {
    if (!templateName.trim() || selectedMuscleGroups.length === 0 || selectedExercises.length === 0) {
      alert(t('workout.templates.modal.requiredFields'));
      return;
    }

    const templateData = {
      name: templateName.trim(),
      description: description.trim(),
      muscleGroups: selectedMuscleGroups,
      exercises: selectedExercises.map(ex => ({
        exerciseId: ex.exerciseId,
        sets: ex.sets,
        reps: ex.reps,
        weight: ex.weight,
        unit: ex.unit,
      })),
      userId: user?.id,
    };

    try {
      if (initialTemplate?.id) {
        await apiServices.templates.updateTemplate(initialTemplate.id, {
          name: templateData.name,
          description: templateData.description,
          muscleGroups: templateData.muscleGroups,
          exercises: templateData.exercises,
        });
      } else {
        await apiServices.templates.createTemplate(templateData);
      }
      
      // Reset form
      setTemplateName('');
      setDescription('');
      setSelectedMuscleGroups([]);
      setSelectedExercises([]);
      
      onTemplateCreated();
      onClose();
    } catch (error) {
      console.error('Failed to create template:', error);
      alert(t('workout.templates.modal.saveFailed'));
    }
  };

  const filteredExercises = searchQuery.trim() ? searchResults : availableExercises;
  const visibleExercises = filteredExercises.slice(0, displayedCount);
  const hasMore = displayedCount < filteredExercises.length;

  const handleExerciseOptionsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore) return;

    const target = e.currentTarget;
    const scrollBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    if (scrollBottom < 80) {
      setDisplayedCount(prev => Math.min(prev + LOAD_BATCH, filteredExercises.length));
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', margin: 'auto' }}>
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label={t('workout.templates.close')}
        >
          ✕
        </button>

        <div className={styles.modalBody}>
          <h2 className={styles.title}>{isEditMode ? t('workout.templates.modal.editTitle') : t('workout.templates.modal.createTitle')}</h2>

          {/* Exercises */}
          <div className={styles.formGroup}>
            <label className={styles.label}>{t('workout.templates.modal.exercises')} * ({selectedExercises.length})</label>
            
            {selectedExercises.length > 0 && (
              <div className={styles.exercisesList}>
                {selectedExercises.map((ex, index) => (
                  <div key={index} className={styles.exerciseItem}>
                    <div className={styles.exerciseHeader}>
                      <div className={styles.exerciseThumb}>
                        {ex.imageUrl ? (
                          <img
                            src={BaseApiService.resolveImageUrl(ex.imageUrl) as string}
                            alt={`${ex.name} preview`}
                            className={styles.exerciseThumbImage}
                            loading="lazy"
                          />
                        ) : (
                          <div className={styles.exerciseThumbPlaceholder}>{t('workout.templates.modal.noImage')}</div>
                        )}
                      </div>
                      <div className={styles.exerciseName}>{ex.name}</div>
                    </div>
                    <div className={styles.exerciseInputs}>
                      <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>{t('workout.templates.modal.sets')}</label>
                        <input
                          type="number"
                          value={ex.sets}
                          onChange={(e) => handleExerciseChange(index, 'sets', parseInt(e.target.value) || 0)}
                          placeholder="3"
                          className={styles.smallInput}
                          min="1"
                        />
                      </div>
                      {isHoldTimeExercise(ex.exerciseType) ? (
                        // TODO.md E, item 5: a single hold-time (seconds) input instead of
                        // reps/weight/unit, stored in the same `reps` field with unit forced to 'sec'.
                        <div className={styles.inputGroup}>
                          <label className={styles.inputLabel}>{t('workout.templates.modal.holdTime')}</label>
                          <input
                            type="number"
                            value={ex.reps}
                            onChange={(e) => {
                              handleExerciseChange(index, 'reps', parseInt(e.target.value) || 0);
                              handleExerciseChange(index, 'weight', 0);
                              handleExerciseChange(index, 'unit', 'sec');
                            }}
                            placeholder="30"
                            className={styles.smallInput}
                            min="1"
                            data-testid={`template-exercise-${index}-hold-time`}
                          />
                        </div>
                      ) : (
                        <>
                          <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>{t('workout.templates.modal.reps')}</label>
                            <input
                              type="number"
                              value={ex.reps}
                              onChange={(e) => handleExerciseChange(index, 'reps', parseInt(e.target.value) || 0)}
                              placeholder="10"
                              className={styles.smallInput}
                              min="1"
                            />
                          </div>
                          <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>{t('workout.templates.modal.weight')}</label>
                            <input
                              type="number"
                              value={ex.weight}
                              onChange={(e) => handleExerciseChange(index, 'weight', parseFloat(e.target.value) || 0)}
                              placeholder="20"
                              className={styles.smallInput}
                              min="0"
                              step="0.5"
                            />
                          </div>
                          <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>{t('workout.templates.modal.unit')}</label>
                            <select
                              value={ex.unit}
                              onChange={(e) => handleExerciseChange(index, 'unit', e.target.value)}
                              className={styles.unitSelect}
                            >
                              <option value="kg">kg</option>
                              <option value="lb">lb</option>
                              <option value="bw">bw</option>
                              <option value="assist">assist</option>
                            </select>
                          </div>
                        </>
                      )}
                      <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>&nbsp;</label>
                        <button
                          type="button"
                          onClick={() => handleRemoveExercise(index)}
                          className={styles.removeBtn}
                          aria-label={t('workout.templates.modal.removeExercise')}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!showExerciseSelector ? (
              <button
                type="button"
                onClick={() => setShowExerciseSelector(true)}
                className={styles.addExerciseBtn}
              >
                + {t('workout.templates.modal.addExercise')}
              </button>
            ) : (
              <div className={styles.exerciseSelector}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('workout.templates.modal.searchPlaceholder')}
                  className={styles.searchInput}
                  autoFocus
                />
                <div className={styles.exerciseOptions} onScroll={handleExerciseOptionsScroll}>
                  {isSearching && (
                    <div className={styles.exerciseOption}>
                      {t('workout.templates.modal.searching')}
                    </div>
                  )}
                  {visibleExercises.map(exercise => (
                    <div
                      key={exercise.id}
                      className={styles.exerciseOption}
                      onClick={() => handleAddExercise(exercise)}
                    >
                      <div className={styles.exerciseOptionLeft}>
                        <div className={styles.exerciseOptionThumb}>
                          {exercise.imageUrl ? (
                            <img
                              src={BaseApiService.resolveImageUrl(exercise.imageUrl) as string}
                              alt={`${exercise.name} preview`}
                              className={styles.exerciseOptionThumbImage}
                              loading="lazy"
                            />
                          ) : (
                            <div className={styles.exerciseOptionThumbPlaceholder}>{t('workout.templates.modal.noImage')}</div>
                          )}
                        </div>
                        <div className={styles.exerciseOptionName}>{exercise.name}</div>
                      </div>
                      <span className={styles.exerciseMuscle}>{exercise.muscleGroup}</span>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowExerciseSelector(false);
                    setSearchQuery('');
                  }}
                  className={styles.cancelBtn}
                >
                  {t('workout.today.cancel')}
                </button>
              </div>
            )}

          {/* Muscle Groups */}
          <div className={styles.formGroup}>
            <label className={styles.label}>{t('workout.templates.modal.targetMuscleGroups')} *</label>
            <div className={styles.muscleGroupGrid}>
              {muscleGroups.map(group => (
                <button
                  key={group}
                  type="button"
                  className={`${styles.muscleGroupBtn} ${selectedMuscleGroups.includes(group) ? styles.active : ''}`}
                  onClick={() => handleMuscleGroupToggle(group)}
                >
                  {formatMuscleGroupLabel(group)}
                </button>
              ))}
            </div>
          </div>

          {/* Template Name */}
          <div className={styles.formGroup}>
            <label className={styles.label}>{t('workout.templates.modal.templateName')} *</label>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder={t('workout.templates.modal.templateNamePlaceholder')}
              className={styles.input}
            />
          </div>

          {/* Description */}
          <div className={styles.formGroup}>
            <label className={styles.label}>{t('workout.templates.modal.description')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('workout.templates.modal.descriptionPlaceholder')}
              rows={3}
              className={styles.textarea}
            />
          </div>

          {/* Private Checkbox */}
          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={true}
                disabled={true}
                className={styles.checkbox}
                style={{ cursor: 'not-allowed', opacity: 0.6 }}
              />
              <span>{t('workout.templates.modal.privateHint')}</span>
            </label>
          </div>
          </div>
          <div className={styles.actions}>
          <button onClick={onClose} className={styles.btnSecondary}>
            {t('workout.today.cancel')}
          </button>
          <button onClick={handleSubmit} className={styles.btnPrimary}>
            {isEditMode ? t('workout.templates.modal.saveChanges') : t('workout.templates.modal.saveTemplate')}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTemplateModal;
